import { getBearerToken } from "../../../../../../api/postCall/bearerToken";
import { CoverMyMedsInputModel } from "../../../../../../data-model/cmmInputRequestModel";
import { API_ENDPOINTS } from "../../../../../../enums/apiUrls";
import type { EvaluationApiResponse } from "./playGroundTypes";

const FIRST_STP_REVIEW_RULES_PATH = "first_stp_review";

export const fetchCmmInputData = async (identifier: string) => {
  const token = await getBearerToken();

  const payload = {
    dataset_name: "oncoemr",
    table_name: "cmm_input",
    order_by: [
      {
        column: "created_at",
        direction: "DESC",
      },
    ],
    filters: {
      identifier: [identifier],
    },
    page_size: 1,
    unnest: true,
  };

  const response = await fetch(API_ENDPOINTS.GET_PHARMA_STP_FILE_DATA, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const data = await response.json();
  const rows: any[] = data?.rows ?? data?.details ?? [];

  if (!rows.length) {
    throw new Error("No order data found for the given identifier");
  }

  return rows[0] as CoverMyMedsInputModel;
};

/** Preview first STP review rules via backend pa-order-update-with-rules. */
export const evaluateFirstStpReviewRules = async (
  payload: CoverMyMedsInputModel,
): Promise<EvaluationApiResponse> => {
  const token = await getBearerToken();

  const response = await fetch(API_ENDPOINTS.EVALUATE_FIRST_STP_REVIEW, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      invocation: "before_stp",
      preview_rows: [payload],
      rules_paths: [FIRST_STP_REVIEW_RULES_PATH],
    }),
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const body = await response.json();
  const updatedRow = body?.updated_rows?.[0];
  const needsReview = updatedRow?.needs_review ?? "Not Needed";
  const reviewReason = updatedRow?.review_reason ?? "";
  const matchedLabel = "First STP review";

  const stepStatus =
    needsReview === "Needed"
      ? "blocked"
      : needsReview === "Error"
        ? "error"
        : "passed";

  return {
    needs_review: needsReview,
    reason: reviewReason,
    config_loaded: body?.success !== false,
    matched_rule_label: matchedLabel,
    details: {
      blocks: [
        {
          block_label: matchedLabel,
          steps: [
            {
              step_id: "first-stp-review",
              label: matchedLabel,
              status: stepStatus,
              active: true,
              blocked: needsReview === "Needed",
              reason: reviewReason || undefined,
            },
          ],
        },
      ],
    },
  };
};
